/**
 * 業務マップの絞り込み — カードを押したときに右のレーンがどう絞られるか。
 *
 * **押す → 絞られる、もう一度押す → 解除。** 左のレーンを選ぶと右のレーンがその配下だけに
 * なり、選んでいないレーンは全件のまま出る。絞るのはステージ・業務フロー・業務の
 * 3 段で、業務ルールのカードは右に絞る先が無いので選択を持たず、押しても何も起きない。
 * 別の画面へは、どのカードも右上の「詳細」で行く。
 *
 * 絞り込みを読取の入力にしない。レーンの件数は 1 画面に並ぶ規模で、押すたびに読取を撃ち
 * 直すと絞り込みの往復ごとにグラフ全体を引き直すことになる。**畳むのはここ 1 箇所**で、
 * 部品と画面は畳んだ結果だけを受け取る。
 *
 * 末尾の Epic → ストーリー → 業務 の 3 段の選択（`OverviewSelection` ほか）は、案件の変更一覧
 * （F4 の `pj-lanes-build.ts`）がまだ旧い欄名で使っている。業務マップは読まない。F4 が 4 配列へ
 * 移ったら、その節ごと消す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップの絞り込み（3 段の選択の切り替えと、選択に応じた 4 レーンの中身の算出・「変更したものだけ見る」で直接の変更だけに絞ること・ステージの鎖ごとのまとまり・業務フローの並び・案件が消した要素の戻し）と、案件の変更一覧が移るまでの旧い 3 段の選択を持つ。
 * @graph-connects pjLaneMarkOf [calls] 「変更したものだけ見る」でカードの印を引く
 */
import type {
  BusinessMapBusiness,
  BusinessMapChain,
  BusinessMapFlow,
  BusinessMapFlowRole,
  BusinessMapRule,
  BusinessMapStage,
  GetBusinessMapOutput,
  PjLaneChange,
} from '@cortex/domain-spec-console';

import { isDirectPjLaneMark, pjLaneMarkOf, type PjLaneMarkIndex } from './pj-lane-marks.js';

/**
 * 押せるレーン 3 段。左から順に並べる — 「押した段より右を外す」はこの並びで決まる。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 押せるレーン 3 段
 * @graph-connects none
 */
export const BUSINESS_MAP_SELECTION_LANES = ['stage', 'flow', 'business'] as const;

/** 押せるレーンの種別。 */
export type BusinessMapSelectionLane = (typeof BUSINESS_MAP_SELECTION_LANES)[number];

/**
 * いま選ばれているカード。
 *
 * 3 つとも別々に持つのは、ステージを選んだまま業務フローを選ぶ動きがあるためである。1 つの
 * 「選択中の id」に畳むと、どのレーンの id なのかを持つ場所がもう 1 つ要る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップでいま選ばれているカード（レーンごとの id）
 * @graph-connects none
 */
export interface BusinessMapSelection {
  /** 選んでいるステージの id */
  readonly stageId?: number;
  /** 選んでいる業務フローの id */
  readonly flowId?: number;
  /** 選んでいる業務の id */
  readonly businessId?: number;
}

/** 何も選んでいない状態。 @graph-connects none */
export const EMPTY_BUSINESS_MAP_SELECTION: BusinessMapSelection = {};

/** レーンの種別から、選択の欄の名前を引く表。 @graph-connects none */
const SELECTION_KEY = {
  stage: 'stageId',
  flow: 'flowId',
  business: 'businessId',
} as const satisfies Record<BusinessMapSelectionLane, keyof BusinessMapSelection>;

/**
 * 上の段を自動で合わせるための引き当て表。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローから属するステージを引く表
 * @graph-connects none
 */
export interface BusinessMapParentIndex {
  /** 業務フローの id → 属するステージの id（属していなければ `null`） */
  readonly stageOfFlow: ReadonlyMap<number, number | null>;
}

/**
 * 上の段を自動で合わせるための引き当て表を作る。
 *
 * @param map 業務マップ
 * @returns 業務フロー → ステージの表
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローから属するステージを引く表を作る。
 * @graph-connects none
 */
export function businessMapParentIndex(map: GetBusinessMapOutput): BusinessMapParentIndex {
  return {
    stageOfFlow: new Map(map.flows.map((flow) => [flow.flowId, flow.stageId])),
  };
}

/**
 * 値の入っている欄だけで選択を組む。
 *
 * `undefined` の欄を残さないのは、「選んでいない」を欄の有無 1 つで表すためである。欄が在って
 * 値が `undefined` の形を混ぜると、同じ状態に 2 つの綴りができる。
 *
 * @graph-connects none
 */
function compactSelection(selection: BusinessMapSelection): BusinessMapSelection {
  const { stageId, flowId, businessId } = selection;
  return {
    ...(stageId === undefined ? {} : { stageId }),
    ...(flowId === undefined ? {} : { flowId }),
    ...(businessId === undefined ? {} : { businessId }),
  };
}

/**
 * 1 つの段の選択を外す。**その段より右の選択も一緒に落とす。**
 *
 * 右を残すと、ステージを外したのにその配下の業務フローが選ばれたままになり、どのステージにも
 * 属さない絞り込みが残る。
 *
 * @param current いまの選択
 * @param lane 外す段
 * @returns 外したあとの選択
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 段 1 つの選択を外し、その段より右の選択も落とす。
 * @graph-connects compactSelection [calls] 値の入っている欄だけで選択を組む
 */
export function clearBusinessMapSelection(
  current: BusinessMapSelection,
  lane: BusinessMapSelectionLane,
): BusinessMapSelection {
  const index = BUSINESS_MAP_SELECTION_LANES.indexOf(lane);
  return compactSelection({
    stageId: index > 0 ? current.stageId : undefined,
    flowId: index > 1 ? current.flowId : undefined,
  });
}

/**
 * カードを押したときの新しい選択を返す。
 *
 * **同じカードをもう一度押したら、その段と右の段を外す。** 違うカードを押したら、その段と
 * 右の段を外してからその段を選ぶ。
 *
 * **上の段は自動で合わせる。** 業務フローを選べばそのステージを選ぶ。下だけを選んだ状態で
 * 上のレーンを見ると、どのステージの話なのかが画面から読めない。**業務を選んでも上は合わせ
 * ない** — 業務は複数の業務フローで共有されるので、上を 1 つに決められない。
 *
 * @param current いまの選択
 * @param lane 押したカードの段
 * @param id 押したカードの id
 * @param parents 上の段を引く表
 * @returns 押したあとの選択
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business カードの押下から次の選択を決める（同じカードの再押下は解除、右の段は落とし、上の段は自動で合わせる）。
 * @graph-connects compactSelection [calls] 値の入っている欄だけで選択を組む
 * @graph-connects clearBusinessMapSelection [calls] 1 つの段の選択を外す
 */
export function toggleBusinessMapSelection(
  current: BusinessMapSelection,
  lane: BusinessMapSelectionLane,
  id: number,
  parents: BusinessMapParentIndex,
): BusinessMapSelection {
  const cleared = clearBusinessMapSelection(current, lane);
  if (current[SELECTION_KEY[lane]] === id) return cleared;

  if (lane === 'stage') return { stageId: id };
  if (lane === 'flow') {
    return compactSelection({ stageId: parents.stageOfFlow.get(id) ?? undefined, flowId: id });
  }
  return { ...cleared, businessId: id };
}

/**
 * 選択に応じて 4 レーンに出す中身。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 選択を当てたあとの 4 レーンの中身（ステージ・業務フロー・業務・業務ルール）
 * @graph-connects none
 */
export interface BusinessMapLanes {
  /** ステージレーンに出すステージ（常に全件。鎖の順 → 横断） */
  readonly stages: readonly BusinessMapStage[];
  /** 業務フローレーンに出す業務フロー（メインとサブの両方。並びは `orderFlowsByStage` が決める） */
  readonly flows: readonly BusinessMapFlow[];
  /** 業務レーンに出す業務 */
  readonly businesses: readonly BusinessMapBusiness[];
  /** 業務ルールレーンに出す業務ルール（名前の順） */
  readonly rules: readonly BusinessMapRule[];
}

/** 姿に在る id だけを選択として読む。姿に無い id は選んでいないのと同じに扱う。 @graph-connects none */
function knownId(id: number | undefined, ids: ReadonlySet<number>): number | undefined {
  return id !== undefined && ids.has(id) ? id : undefined;
}

/** 名前の順（日本語の照合）に並べ替えた写しを返す。 @graph-connects none */
function byLabel<T extends { label: string }>(items: readonly T[]): T[] {
  return [...items].sort((left, right) => left.label.localeCompare(right.label, 'ja'));
}

/**
 * 選択を当てた 4 レーンの中身を作る。
 *
 * - ステージは常に全件（読取が返した鎖の順のまま）
 * - 業務フローはステージを選べばその配下だけ
 * - 業務は業務フローを選べばその業務フローが含むものだけ、業務フローを選ばずステージを選べば
 *   表示中の業務フローが含むものの和
 * - 業務ルールは業務を選べばその業務の分、選ばなければ表示中の業務の業務ルールの和
 *
 * **業務と業務ルールは重複を除く** — 同じ業務が複数の業務フローに、同じ業務ルールが複数の業務に
 * 出るので、そのまま並べると同じカードが何枚も出る。
 *
 * **左を何も選んでいなければ業務は全件を出す。** 業務フロー経由で絞ると、どの業務フローにも
 * 含まれていない業務がレーンから消え、「登録はされているがどの流れからも呼ばれていない」という
 * 実データの穴が画面から見えなくなる。レーンの件数は一覧（`list_spec`）の件数と一致していな
 * ければならない。
 *
 * **業務ルールは名前の順に並べる。** 業務が持つ並びのままにすると、業務を選び替えるたびに同じ
 * 業務ルールの位置が変わり、和を取ったときの並びが業務の順番に引きずられる。
 *
 * @param map 業務マップ
 * @param selection いまの選択
 * @returns 4 レーンに出す中身
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 選択を当てて 4 レーンの中身（ステージ・業務フロー・業務・業務ルール）を決める。
 * @graph-connects knownId [calls] 姿に在る id だけを選択として読む
 * @graph-connects byLabel [calls] 名前の順（日本語の照合）に並べ替えた写しを返す
 */
export function selectBusinessMapLanes(
  map: GetBusinessMapOutput,
  selection: BusinessMapSelection,
): BusinessMapLanes {
  const stageId = knownId(selection.stageId, new Set(map.stages.map((stage) => stage.stageId)));
  const flowsById = new Map(map.flows.map((flow) => [flow.flowId, flow]));
  const selectedFlow = selection.flowId === undefined ? undefined : flowsById.get(selection.flowId);
  const businessesById = new Map(map.businesses.map((business) => [business.businessId, business]));
  const selectedBusiness =
    selection.businessId === undefined ? undefined : businessesById.get(selection.businessId);
  const rulesById = new Map(map.rules.map((rule) => [rule.ruleId, rule]));

  const flows =
    stageId === undefined ? map.flows : map.flows.filter((flow) => flow.stageId === stageId);

  const businessSource =
    selectedFlow !== undefined ? [selectedFlow] : stageId === undefined ? null : flows;
  const businessIds =
    businessSource === null ? null : new Set(businessSource.flatMap((flow) => flow.businessIds));
  const businesses =
    businessIds === null
      ? map.businesses
      : map.businesses.filter((business) => businessIds.has(business.businessId));

  const ruleIds = new Set(
    (selectedBusiness === undefined ? businesses : [selectedBusiness]).flatMap(
      (business) => business.ruleIds,
    ),
  );
  const rules = byLabel(
    [...ruleIds].flatMap((ruleId) => {
      const rule = rulesById.get(ruleId);
      return rule === undefined ? [] : [rule];
    }),
  );

  return { stages: map.stages, flows, businesses, rules };
}

/**
 * 「変更したものだけ見る」で、4 レーンを案件が直接変えた要素だけに絞る（Dhik 2026-09-29「PJモードのとき業務
 * マップ、画面一覧の右上に全体を出すか変更されたものだけを出すかのトグルボタンをつけてほしい」）。
 *
 * **配下に変更（`descendant`）は出さない。** 自身の変更の行を持たず、配下が変わったことを伝える
 * だけの印で、案件の変更一覧と同じく「change に紐付けられた変更」に当たらない。案件が消した要素は
 * 「削除」の印を持つので残る（`withDeletedFromMaster` が戻したもの）。
 *
 * **選択を当てたあとに絞る**（`selectBusinessMapLanes` の結果を受け取る）。先に姿から変わっていない
 * 要素を抜くと、変わっていない業務フローの配下の変わった業務が、ステージを選んだときに業務の
 * レーンから落ちる — 業務は業務フローを経由して引くためである。絞り込みは全体の姿のまま効かせ、
 * 出すカードだけを印で選ぶ。
 *
 * @param lanes 選択を当てた 4 レーンの中身
 * @param marks 案件で変わった要素の印の索引
 * @returns 直接の変更の印を持つ要素だけの 4 レーン
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 選択を当てた 4 レーンを、案件が直接変えた（追加・変更・削除の印を持つ）ステージ・業務フロー・業務・業務ルールだけに絞る。
 * @graph-connects pjLaneMarkOf [calls] カード 1 枚の印を引く
 * @graph-connects isDirectPjLaneMark [calls] 印が直接の変更かの判定
 */
export function keepDirectlyChangedLanes(
  lanes: BusinessMapLanes,
  marks: PjLaneMarkIndex,
): BusinessMapLanes {
  const direct = (id: number) => isDirectPjLaneMark(pjLaneMarkOf(marks, id));

  return {
    stages: lanes.stages.filter((stage) => direct(stage.stageId)),
    flows: lanes.flows.filter((flow) => direct(flow.flowId)),
    businesses: lanes.businesses.filter((business) => direct(business.businessId)),
    rules: lanes.rules.filter((rule) => direct(rule.ruleId)),
  };
}

/**
 * ステージレーンの 1 まとまり — 鎖 1 本ぶんのステージ、または横断ステージ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージレーンに並べる鎖 1 本ぶん（または横断）のステージと、切れ目に出す鎖の名前
 * @graph-connects none
 */
export interface BusinessMapStageGroup {
  /** まとまりの鍵 */
  readonly key: string;
  /** 鎖。横断ステージのまとまりは `null` */
  readonly chain: BusinessMapChain | null;
  /** 並べるステージ（鎖の順） */
  readonly stages: readonly BusinessMapStage[];
}

/**
 * ステージを鎖ごとのまとまりに分ける。
 *
 * 並びは読取の `chains` の順で、鎖の中は `chains[].stageIds` の順である。**鎖の組み立てを
 * 画面で繰り返さない** — 鎖は共通部の 1 関数が導出したものを読取が返しており、画面が入口を
 * 辿り直すと切り方が食い違う。どの鎖にも入らないステージ（横断）は最後の 1 まとまりに置く。
 *
 * @param stages ステージレーンに出すステージ
 * @param chains 読取が返した鎖
 * @returns 鎖ごとのまとまり（横断は最後）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージを読取が返した鎖ごとのまとまりに分け、横断ステージを最後に置く。
 * @graph-connects none
 */
export function groupBusinessMapStages(
  stages: readonly BusinessMapStage[],
  chains: readonly BusinessMapChain[],
): readonly BusinessMapStageGroup[] {
  const stagesById = new Map(stages.map((stage) => [stage.stageId, stage]));
  const placed = new Set<number>();
  const groups: BusinessMapStageGroup[] = chains.flatMap((chain, index) => {
    const members = chain.stageIds.flatMap((stageId) => {
      const stage = stagesById.get(stageId);
      if (stage === undefined || placed.has(stageId)) return [];
      placed.add(stageId);
      return [stage];
    });
    return members.length === 0 ? [] : [{ key: `chain-${String(index)}`, chain, stages: members }];
  });
  const rest = stages.filter((stage) => !placed.has(stage.stageId));
  return rest.length === 0 ? groups : [...groups, { key: 'cross', chain: null, stages: rest }];
}

/**
 * 案件を当てた姿に、案件が消した要素を master の姿から戻す。
 *
 * **案件モードでは消える要素も「削除」の印で出す**（引継書 §3.1 のモックの置き方。業務ルールを
 * 出すかは未決で、モックの今の置き方に従う）。案件を当てた姿には消えた要素が居ないので、master の
 * 姿から拾って足す。足すのは印の読取（`laneChanges`）が「削除」と言った要素だけで、どれが消えたかを
 * ここで比べ直さない。
 *
 * **親の配下にも戻す。** 消えた業務は、master でそれを含んでいた業務フローの `businessIds` に足す
 * （業務ルール・業務フローも同じ）。戻さないと、親を選んだときに消える要素が絞り込みから落ち、
 * 業務ルールのレーンは業務の配下からしか引かないので、消えた業務ルールが 1 枚も出ない。
 *
 * 「削除」はレーンの型ごとに分けて持つ。id がグラフ全体で一意であることに頼らず、業務の「削除」で
 * 同じ番号の業務ルールを拾うような取り違えを型で塞ぐ。
 *
 * @param after 案件を当てた姿
 * @param master master の姿
 * @param laneChanges 案件で変わったレーン 4 型（共通部が決めたもの）
 * @returns 消えた要素を足した姿。消えた要素が無ければ `after` そのもの
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件を当てた業務マップに、案件が消したステージ・業務フロー・業務・業務ルールを master から戻し、親の配下にも戻す。
 * @graph-connects none
 */
export function withDeletedFromMaster(
  after: GetBusinessMapOutput,
  master: GetBusinessMapOutput,
  laneChanges: readonly PjLaneChange[],
): GetBusinessMapOutput {
  const deletedOf = (nodeType: PjLaneChange['nodeType']): ReadonlySet<number> =>
    new Set(
      laneChanges
        .filter((change) => change.nodeType === nodeType && change.changeKind === 'deleted')
        .map((change) => change.nodeId),
    );
  const deleted = {
    stage: deletedOf('Stage'),
    flow: deletedOf('Flow'),
    business: deletedOf('Business'),
    rule: deletedOf('Rule'),
  };
  if (Object.values(deleted).every((ids) => ids.size === 0)) return after;

  /* master にあって、案件を当てた姿に居ない「削除」の要素だけを拾う。 */
  const missing = <T>(
    items: readonly T[],
    present: readonly T[],
    idOf: (item: T) => number,
    gone: ReadonlySet<number>,
  ) => {
    const here = new Set(present.map(idOf));
    return items.filter((item) => gone.has(idOf(item)) && !here.has(idOf(item)));
  };
  /* 親の配下へ、master で配下だった「削除」の子を足す。 */
  const restore = (
    children: readonly number[],
    masterChildren: readonly number[] | undefined,
    gone: ReadonlySet<number>,
  ) => [
    ...children,
    ...(masterChildren ?? []).filter((id) => gone.has(id) && !children.includes(id)),
  ];
  const masterStage = new Map(master.stages.map((stage) => [stage.stageId, stage]));
  const masterFlow = new Map(master.flows.map((flow) => [flow.flowId, flow]));
  const masterBusiness = new Map(master.businesses.map((item) => [item.businessId, item]));

  return {
    ...after,
    stages: [
      ...after.stages.map((stage) => ({
        ...stage,
        flowIds: restore(stage.flowIds, masterStage.get(stage.stageId)?.flowIds, deleted.flow),
      })),
      ...missing(master.stages, after.stages, (stage) => stage.stageId, deleted.stage),
    ],
    flows: [
      ...after.flows.map((flow) => ({
        ...flow,
        businessIds: restore(
          flow.businessIds,
          masterFlow.get(flow.flowId)?.businessIds,
          deleted.business,
        ),
      })),
      ...missing(master.flows, after.flows, (flow) => flow.flowId, deleted.flow),
    ],
    businesses: [
      ...after.businesses.map((business) => ({
        ...business,
        ruleIds: restore(
          business.ruleIds,
          masterBusiness.get(business.businessId)?.ruleIds,
          deleted.rule,
        ),
      })),
      ...missing(
        master.businesses,
        after.businesses,
        (business) => business.businessId,
        deleted.business,
      ),
    ],
    rules: [
      ...after.rules,
      ...missing(master.rules, after.rules, (rule) => rule.ruleId, deleted.rule),
    ],
  };
}

/**
 * 業務フローの並びを決める材料（属するステージと型）。業務マップと案件の変更一覧の両方の業務
 * フローが満たす形である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローを並べるのに使う欄（属するステージ・型）
 * @graph-connects none
 */
export interface OrderableFlow {
  /** 属するステージの id。属していなければ `null` */
  readonly stageId: number | null;
  /** 業務フローの型。欄が読めなければ `null` */
  readonly role: BusinessMapFlowRole | null;
}

/**
 * 業務フローを、ステージの順に、ステージの中はメインフローを一番上に並べる。
 *
 * **畳まずに全部出す**（引継書 §3.1。2026-09-27 に畳むのをやめた）。メインフローはステージにつき
 * 1 本で、畳むと出すものがほぼ残らない。並びだけでメインと分かるようにし、型はカードの補足
 * （`flowKindLabel`）で示す。
 *
 * ステージの順は渡した `stageIds` の順（業務マップは鎖の順）。ステージに属さない業務フローと、
 * 並びに無いステージの業務フローは最後に置く。サブフローどうし・ステージの外どうしは読取が返した
 * 順のまま動かさない — 並べ替えのたびに位置が揺れると、読み手が前に見た位置を頼りにできない。
 *
 * **業務マップと案件の変更一覧が同じ関数を通す**（W1-9「PJ の変更一覧の業務フローのレーンが業務
 * マップと同じ並び」）。
 *
 * @param flows 業務フローレーンに出す業務フロー
 * @param stageIds ステージの並び（業務フローの並びを決める）
 * @returns 並べ直した写し
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローをステージの順に並べ、ステージの中はメインフローを一番上、その下にサブフローを読取の順で並べる。
 * @graph-connects none
 */
export function orderFlowsByStage<T extends OrderableFlow>(
  flows: readonly T[],
  stageIds: readonly number[],
): readonly T[] {
  const rankOf = new Map(stageIds.map((stageId, index) => [stageId, index]));
  /* 並びに無いステージと、ステージに属さない業務フローは最後（並びの長さ）に置く。 */
  const stageRank = (flow: T) =>
    flow.stageId === null ? stageIds.length : (rankOf.get(flow.stageId) ?? stageIds.length);
  const mainRank = (flow: T) => (flow.role === 'main' ? 0 : 1);

  // Array.prototype.sort は安定なので、同じ順位の業務フローは読取の順のまま残る
  return [...flows].sort(
    (left, right) => stageRank(left) - stageRank(right) || mainRank(left) - mainRank(right),
  );
}

/*
 * ここから下は、案件の変更一覧（F4）が旧い欄名（Epic・ストーリー）で読んでいる間だけ残す 3 段の
 * 選択である。業務マップの画面は使わない。
 */

/** 案件の変更一覧で押せる階層のレーンの種別（移行の間の形）。 */
export type OverviewSelectionLane = 'epic' | 'userStory' | 'business';

/**
 * 案件の変更一覧でいま選ばれている階層のカード（移行の間の形）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更一覧が移るまで、Epic・ストーリー・業務の選択を旧い欄名で持つ
 * @graph-connects none
 */
export interface OverviewSelection {
  /** 選んでいる Epic の id */
  readonly epicId?: number;
  /** 選んでいるストーリーの id */
  readonly userStoryId?: number;
  /** 選んでいる業務の id */
  readonly businessId?: number;
}

/**
 * 旧い 3 段の選択で、カードを押したときの新しい選択を返す（移行の間の形）。
 *
 * 同じカードをもう一度押したら解除し、左を外すと右も落とす。ストーリーを押したら、その
 * ストーリーが属する Epic も一緒に選ぶ。
 *
 * @param current いまの選択
 * @param lane 押したカードのレーン
 * @param id 押したカードの id
 * @param epicOfUserStory ストーリーの id から属する Epic を引く表
 * @returns 押したあとの選択
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更一覧が移るまで、Epic・ストーリー・業務の押下から次の選択を決める。
 * @graph-connects none
 */
export function toggleOverviewSelection(
  current: OverviewSelection,
  lane: OverviewSelectionLane,
  id: number,
  epicOfUserStory: ReadonlyMap<number, number | null>,
): OverviewSelection {
  if (lane === 'epic') {
    return current.epicId === id ? {} : { epicId: id };
  }
  if (lane === 'userStory') {
    if (current.userStoryId === id) return clearOverviewSelection(current, 'userStory');
    const epicId = epicOfUserStory.get(id) ?? undefined;
    return { epicId, userStoryId: id };
  }
  if (current.businessId === id) return clearOverviewSelection(current, 'business');
  return { epicId: current.epicId, userStoryId: current.userStoryId, businessId: id };
}

/**
 * 旧い 3 段の選択で、1 つのレーンの選択を外す（移行の間の形）。そのレーンより右も落とす。
 *
 * @param current いまの選択
 * @param lane 外すレーン
 * @returns 外したあとの選択
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更一覧が移るまで、Epic・ストーリー・業務のレーン 1 つの選択を外し、右も落とす。
 * @graph-connects none
 */
export function clearOverviewSelection(
  current: OverviewSelection,
  lane: OverviewSelectionLane,
): OverviewSelection {
  if (lane === 'epic') return {};
  if (lane === 'userStory') return current.epicId === undefined ? {} : { epicId: current.epicId };
  return { epicId: current.epicId, userStoryId: current.userStoryId };
}
